import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { LucideIcon } from 'components/LucideIcon'
import { formatDistanceToNow, parseISO } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { useNotificationPreferences } from './useNotificationPreferences'
import { useNotifications } from './useNotifications'

const parseTimestamp = (timestamp: string): Date => {
  const hasTimezone =
    /[Z+-]\d{2}:?\d{2}$/.test(timestamp) || timestamp.endsWith('Z')

  if (!hasTimezone) {
    return parseISO(timestamp + 'Z')
  }
  return parseISO(timestamp)
}

const selectedButtonStyles = css`
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: bold;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeButton};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const NotificationsContainer = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: ${({ theme }) => theme.forecast.background2_5};
  border-radius: 50%;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    transform: scale(1.05);
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.background4};
    transform: scale(0.95);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.activeButton};
    outline-offset: 2px;
  }

  ${({ $isSelected }) => $isSelected && selectedButtonStyles}
`

const StyledDropdownMenuTrigger = styled(DropdownMenu.Trigger)`
  cursor: pointer;
`

const StyledDropdownMenuContent = styled(DropdownMenu.Content)`
  width: 384px;
  max-height: 400px;
  padding: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 0.5rem;
  box-shadow:
    0 0.625rem 2.375rem -0.625rem rgba(22, 23, 24, 0.35),
    0 0.625rem 1.25rem -0.9375rem rgba(22, 23, 24, 0.2);
`

const NotificationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  margin-bottom: 0.5rem;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const MarkAllReadButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.background3};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ToggleLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  user-select: none;
`

const Slider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.forecast.background3};
  transition: 0.2s;
  border-radius: 24px;
  pointer-events: none;
  z-index: 1;

  &:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.2s;
    border-radius: 50%;
  }
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
  user-select: none;

  input {
    position: absolute;
    opacity: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    cursor: pointer;
    z-index: 2;
  }

  input:checked + ${Slider} {
    background-color: ${({ theme }) =>
      theme.forecast.primary ?? theme.forecast.defaultButton};
  }

  input:checked + ${Slider}:before {
    transform: translateX(20px);
  }

  &:hover ${Slider} {
    background-color: ${({ theme }) => theme.forecast.background4};
  }

  input:checked ~ ${Slider}, input:checked:hover ~ ${Slider} {
    background-color: ${({ theme }) =>
      theme.forecast.primary ?? theme.forecast.defaultButton};
    opacity: 0.9;
  }
`

const NotificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 320px;
  overflow-y: auto;
`

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const LoadingState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ErrorState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.danger ?? theme.common.alert.error};
`

const NotificationBadge = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: ${({ theme }) =>
    theme.forecast.danger ?? theme.common.alert.error};
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  font-weight: 600;
  border: 2px solid ${({ theme }) => theme.forecast.background2};
`

const NotificationContainer = styled.div`
  position: relative;
  display: inline-block;
`

const NotificationTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.25rem;
  line-height: 1.4;
`

const NotificationBody = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.375rem;
`

const NotificationTimestamp = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin-top: 0.25rem;
`

const NotificationItemContainer = styled.div<{ $isUnread: boolean }>`
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 0.25rem;
  background-color: ${({ theme, $isUnread }) =>
    $isUnread ? theme.forecast.background2_5 : theme.forecast.background2};
  border-left: 3px solid
    ${({ theme, $isUnread }) =>
      $isUnread
        ? (theme.forecast.primary ?? theme.forecast.defaultButton)
        : 'transparent'};
  opacity: ${({ $isUnread }) => ($isUnread ? 1 : 0.7)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`

const DismissButton = styled.button`
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transition: all 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background4};
    border-color: ${({ theme }) => theme.forecast.border};
    color: ${({ theme }) => theme.forecast.activeText};
  }

  &:active {
    transform: scale(0.98);
    background-color: ${({ theme }) => theme.forecast.background2_5};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.activeButton};
    outline-offset: 2px;
  }
`

export const NotificationsDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const { preferences } = useNotificationPreferences({
    enabled: true,
  })
  const arePlatformNotificationsEnabled = preferences.channels.inapp

  const { unreadCount } = useNotifications({ enabled: true })
  const { notifications, isLoading, error, updateNotificationStatus } =
    useNotifications({
      enabled: isOpen,
      status: showAll ? undefined : 'unread',
    })

  const handleMarkAsRead = useCallback(
    async (e: React.MouseEvent, notificationId: string) => {
      e.stopPropagation()
      try {
        await updateNotificationStatus(notificationId, 'read')
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    },
    [updateNotificationStatus]
  )

  const handleNotificationClick = useCallback(
    async (
      e: React.MouseEvent,
      notification: { id: string; cta?: { label: string; url: string } }
    ) => {
      e.stopPropagation()

      try {
        await updateNotificationStatus(notification.id, 'read')
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }

      if (notification.cta?.url) {
        window.open(notification.cta.url, '_blank', 'noopener,noreferrer')
      }
    },
    [updateNotificationStatus]
  )

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter((n) => n.isUnread)
    if (unreadNotifications.length === 0) return

    try {
      await Promise.all(
        unreadNotifications.map((notification) =>
          updateNotificationStatus(notification.id, 'read')
        )
      )
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }, [notifications, updateNotificationStatus])

  const sortedNotifications = useMemo(() => {
    const filtered = showAll
      ? notifications
      : notifications.filter((n) => n.isUnread)
    if (showAll) {
      return [...filtered].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    }
    return filtered
  }, [notifications, showAll])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) setShowAll(false)
  }

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <StyledDropdownMenuTrigger asChild>
        <NotificationContainer>
          <NotificationsContainer
            $isSelected={isOpen}
            tabIndex={0}
            role='button'
            aria-label='Open notifications'
            aria-expanded={isOpen}
            onClick={() => setIsOpen((o) => !o)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setIsOpen((o) => !o)
              }
            }}>
            <LucideIcon name='Bell' size={18} />
          </NotificationsContainer>
          {arePlatformNotificationsEnabled && unreadCount > 0 && (
            <NotificationBadge>{unreadCount}</NotificationBadge>
          )}
        </NotificationContainer>
      </StyledDropdownMenuTrigger>

      <DropdownMenu.Portal>
        <StyledDropdownMenuContent sideOffset={8} align='end'>
          <NotificationsHeader>
            <span>Notifications</span>
            <HeaderActions>
              {notifications.filter((n) => n.isUnread).length > 0 && (
                <MarkAllReadButton
                  onClick={handleMarkAllAsRead}
                  type='button'
                  aria-label='Mark all as read'>
                  Mark all as read
                </MarkAllReadButton>
              )}
              <ToggleContainer>
                <ToggleLabel>{showAll ? 'All' : 'Unread'}</ToggleLabel>
                <ToggleSwitch>
                  <input
                    type='checkbox'
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                    aria-label='Toggle between all and unread notifications'
                  />
                  <Slider />
                </ToggleSwitch>
              </ToggleContainer>
            </HeaderActions>
          </NotificationsHeader>

          {isLoading ? (
            <LoadingState>Loading notifications...</LoadingState>
          ) : error ? (
            <ErrorState>Failed to load notifications</ErrorState>
          ) : sortedNotifications.length === 0 ? (
            <EmptyState>
              {showAll ? 'No notifications' : 'No unread notifications'}
            </EmptyState>
          ) : (
            <NotificationsList>
              {sortedNotifications.map((notification) => (
                <NotificationItemContainer
                  key={notification.id}
                  $isUnread={notification.isUnread}
                  onClick={(e) => handleNotificationClick(e, notification)}>
                  <NotificationContent>
                    {notification.title && (
                      <NotificationTitle>
                        {notification.title}
                      </NotificationTitle>
                    )}
                    {notification.body && (
                      <NotificationBody>{notification.body}</NotificationBody>
                    )}
                    {!notification.title && !notification.body && (
                      <NotificationBody>
                        {notification.message}
                      </NotificationBody>
                    )}
                    {notification.delivered_at && (
                      <NotificationTimestamp>
                        {formatDistanceToNow(
                          parseTimestamp(notification.delivered_at),
                          {
                            addSuffix: true,
                          }
                        )}
                      </NotificationTimestamp>
                    )}
                    {!notification.delivered_at && notification.created_at && (
                      <NotificationTimestamp>
                        {formatDistanceToNow(
                          parseTimestamp(notification.created_at),
                          {
                            addSuffix: true,
                          }
                        )}
                      </NotificationTimestamp>
                    )}
                  </NotificationContent>
                  {notification.isUnread && (
                    <DismissButton
                      onClick={(e) => handleMarkAsRead(e, notification.id)}
                      type='button'
                      aria-label='Dismiss notification'>
                      <span>Dismiss</span>
                    </DismissButton>
                  )}
                </NotificationItemContainer>
              ))}
            </NotificationsList>
          )}
        </StyledDropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
